import type { Dispatch } from 'react'
import type {
  ICountry,
  IDistrict,
  IProvince,
  ISubDistrict,
} from 'features/Address/model'
import type { IIndexable } from 'helpers/model'
import type { Address, BranchForForm } from 'types'

import { isEmpty } from 'lodash'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useRecoilValue } from 'recoil'

import { getAddressField } from 'helpers/common'
import { branchFormState } from 'recoils/atoms/branchFormAtom'
import { CONTACT_TYPE } from 'types'
import * as utils from 'helpers/getAddress'

type Props = (
  countries: ICountry[],
  setProvinces: Dispatch<IProvince[]>,
  setDistricts: Dispatch<IDistrict[]>,
  setSubDistricts: Dispatch<ISubDistrict[]>,
  setIsProvincesLoading: Dispatch<boolean>,
  setIsDistrictsLoading: Dispatch<boolean>,
  setIsSubDistrictsLoading: Dispatch<boolean>,
) => void

export const useIsSameAddress: Props = (
  countries,
  setProvinces,
  setDistricts,
  setSubDistricts,
  setIsProvincesLoading,
  setIsDistrictsLoading,
  setIsSubDistrictsLoading,
) => {
  const { selectedCompany } = useRecoilValue(branchFormState)
  const { setValue, watch } = useFormContext<BranchForForm>()
  const { companyId, isSameAsCompany } = watch(['companyId', 'isSameAsCompany'])

  const handlerAddress = async () => {
    setIsProvincesLoading(true)
    setIsDistrictsLoading(true)
    setIsSubDistrictsLoading(true)

    const address: Address & IIndexable = selectedCompany.addresses[0]
    const emailContact = selectedCompany.contacts.find(
      (contact) => contact.type === CONTACT_TYPE.EMAIL,
    )

    setValue('address', address.address || '')
    setValue('postalCode', address.postalCode || '')
    setValue('email', emailContact?.email || '')

    const country = getAddressField(countries, 'country', address)
    setValue('country', country)

    const { provinces } = await utils.getProvinces(country.id)
    const province = getAddressField(provinces, 'province', address)
    setValue('province', province)
    setProvinces(provinces)
    setIsProvincesLoading(false)

    const { districts } = await utils.getDistricts(province.id)
    const district = getAddressField(districts, 'district', address)
    setValue('district', district)
    setDistricts(districts)
    setIsDistrictsLoading(false)

    const { subDistricts } = await utils.getSubDistricts(district.id)
    const subDistrict = getAddressField(subDistricts, 'subDistrict', address)
    setValue('subDistrict', subDistrict)
    setSubDistricts(subDistricts)
    setIsSubDistrictsLoading(false)
  }

  useEffect(() => {
    if (!isSameAsCompany || !companyId || isEmpty(selectedCompany)) {
      return
    }

    handlerAddress()
  }, [companyId, isSameAsCompany, selectedCompany])
}
